import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './Castle.css';

/**
 * Full-screen shatter effect for castle promotions.
 * Spawns 60 eight-pointed Islamic stars (نجمة رُبع الحزب) that fly outward
 * from the center of the viewport in a choreographed arc, fading elegantly.
 */

const SHARD_COUNT = 60;

// Eight-pointed Islamic star — subtle, sacred, unmistakable
const IslamicStar = ({ size, color }) => (
  <svg width={size} height={size} viewBox="-12 -12 24 24" style={{ display: 'block' }}>
    <path
      d="M 0 -11 L 3 -4.5 L 9.5 -6 L 6 0 L 9.5 6 L 3 4.5 L 0 11 L -3 4.5 L -9.5 6 L -6 0 L -9.5 -6 L -3 -4.5 Z"
      fill={color}
      opacity="0.92"
    />
    <path
      d="M 0 -7 L 2 -2.5 L 6.5 -3.5 L 4 0 L 6.5 3.5 L 2 2.5 L 0 7 L -2 2.5 L -6.5 3.5 L -4 0 L -6.5 -3.5 L -2 -2.5 Z"
      fill="white"
      opacity="0.35"
    />
  </svg>
);

const CastleShatter = ({ color = '#C19A6B', onDone }) => {
  const shards = useMemo(() => {
    return Array.from({ length: SHARD_COUNT }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const distance = 45 + Math.random() * 40; // vh units
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance * 0.7 - 8; // bias upward
      const rotate = (Math.random() - 0.5) * 720;
      const delay = Math.random() * 180;
      const duration = 1800 + Math.random() * 900;
      const size = 10 + Math.random() * 22;
      const depth = Math.random();
      return {
        id: i,
        dx, dy, rotate, delay, duration, size, depth,
      };
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => onDone && onDone(), 2900);
    return () => clearTimeout(t);
  }, [onDone]);

  const content = (
    <div className="castle-shatter-overlay" data-testid="castle-shatter">
      {/* Golden flash */}
      <div
        className="castle-shatter-flash"
        style={{ background: `radial-gradient(circle at center, ${color}55 0%, transparent 60%)` }}
      />
      {shards.map((s) => (
        <div
          key={s.id}
          className="castle-shard"
          style={{
            top: '50%',
            left: '50%',
            ['--dx']: `${s.dx}vw`,
            ['--dy']: `${s.dy}vh`,
            ['--rot']: `${s.rotate}deg`,
            ['--delay']: `${s.delay}ms`,
            ['--duration']: `${s.duration}ms`,
            opacity: 0.6 + s.depth * 0.4,
          }}
        >
          <IslamicStar size={s.size} color={color} />
        </div>
      ))}
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
};

export default CastleShatter;
